import { ChangeEvent, useEffect, useMemo, useRef } from 'react';

import { AccessCodeValidationResult, Transaction, TransactionApi } from 'apis/TransactionApi';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { StudentGroupApi, StudentGroupWithDetails } from 'apis/StudentGroupApi';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { GMetrixStepper } from 'components/GMetrixStepper/GMetrixStepper';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import GMetrixBanner from 'components/GMetrixBanner/GMetrixBanner';
import { helpContent } from 'pages/students/pages/import/help';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { Student, StudentApi } from 'apis/StudentApi';
import { useNotification } from 'app/notifications';
import { PageHeader } from 'components/layout/page';
import { DefaultLayout } from 'components/layout';
import { KendoIntlProvider } from 'app/culture';
import { useNavigate } from 'react-router-dom';
import { validatePassword } from 'utils';
import { useSuperState } from 'hooks';
import Papa from 'papaparse';
import { useApp } from 'app';

import 'styles/kendo-default-theme-with-overrides.scss';
import 'pages/students/pages/import/styles/Import.scss';
import 'styles/global-styles.scss';

interface Field {
    key: string;
    label: string;
}
interface StudentRow {
    [key: string]: string;
    APIData?: any
}
interface ValidationErrors {
    [rowIndex: number]: { [key: string]: string };
}
interface ImportResult {
    added: number;
    created: number;
    skipped: number;
    updated: number;
}

const REQUIRED_FIELDS: Field[] = [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'username', label: 'Username' },
    { key: 'email', label: 'Email Address' },
    { key: 'password', label: 'Password' }
];
const OPTIONAL_FIELDS: Field[] = [
    { key: 'code', label: 'Code' },
    { key: 'studentNumber', label: 'Student Number' }
];
const ALL_FIELDS: Field[] = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS];
const steps = ['Upload', 'Match', 'Repair', 'Complete'];

export const ImportStudents = props => {

    const [state, setState] = useSuperState({
        allAccessCodes: [] as Transaction[],
        allClassrooms: [] as StudentGroupWithDetails[],
        csvData: [] as StudentRow[],
        deletedRowCount: 0,
        errors: {} as ValidationErrors,
        headers: [] as string[],
        hoveredTrashRow: null as number | null,
        initiallyErrored: {} as { [index: number]: boolean },
        isDragging: false,
        lastDeletedRow: null as {
            error?: any;
            index: number;
            initiallyErrored?: boolean;
            row: StudentRow;
        } | null,
        loading: false,
        lockedErrorIndexes: [] as number[],
        mapping: {} as { [key: string]: string },
        result: { added: 0, created: 0, skipped: 0, updated: 0 } as ImportResult,
        rows: [] as StudentRow[],
        showGame: false,
        showOnlyErrors: false,
        step: 1,
        updateExisting: false
    });

    const { selectedPartner } = useApp();
    const navigate = useNavigate();

    const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);
    const urlCode = useMemo(() => urlParams.get('code') || '', []);
    const urlContext = useMemo(() => urlParams.get('context') || '', []);
    const urlGroupId = useMemo(() => urlParams.get('groupId') || '', []);
    const urlAccessCodeId = useMemo(() => urlParams.get('transactionId') || '', []);
    const isScoped = urlContext === 'group' || urlContext === 'accessCodes';
    const activeFields = isScoped ? ALL_FIELDS.filter(f => f.key !== 'code') : ALL_FIELDS;
    const matchRequiredFields = isScoped ? REQUIRED_FIELDS.filter(f => f.key === 'username') : REQUIRED_FIELDS;

    const undoTimerRef = useRef<NodeJS.Timeout | null>(null);

    const scheduleUndoToastClear = () => {

        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        undoTimerRef.current = setTimeout(() => {

            setState({ lastDeletedRow: null });

        }, 5000);

    };

    const deleteRow = (rowIndex: number) => {

        const deletedRow = state.rows[rowIndex];
        const deletedError = state.errors[rowIndex];
        const deletedInitiallyErrored = state.initiallyErrored[rowIndex];

        // Remove the row
        const newRows = state.rows.filter((_, idx) => idx !== rowIndex);

        // Reindex errors
        const newErrors: ErrorMap = {};
        Object.entries(state.errors).forEach(([key, val]) => {

            const idx = Number(key);
            if (idx === rowIndex) return;
            const newIdx = idx > rowIndex ? idx - 1 : idx;
            newErrors[newIdx] = val;

        });

        // Reindex initially errored map
        const newInitiallyErrored: { [index: number]: boolean } = {};
        Object.entries(state.initiallyErrored).forEach(([key, val]) => {

            const idx = Number(key);
            if (idx === rowIndex) return;
            const newIdx = idx > rowIndex ? idx - 1 : idx;
            newInitiallyErrored[newIdx] = val;

        });

        setState({ errors: newErrors, initiallyErrored: newInitiallyErrored, lockedErrorIndexes: [], rows: newRows });
        if (isScoped) setState(prev => ({ deletedRowCount: prev.deletedRowCount + 1 }));

        setState({ lastDeletedRow: { error: deletedError, index: rowIndex, initiallyErrored: deletedInitiallyErrored, row: deletedRow } });
        scheduleUndoToastClear();

        // Show toast notification with undo button
        // Capture the deleted row data in the closure
        const capturedDeletedData = { error: deletedError, index: rowIndex, initiallyErrored: deletedInitiallyErrored, row: deletedRow };

        createToast(
            formatMessage({ id: 'row_removed_click_undo_to_restore.cc45526f', defaultMessage: 'Row removed. Click undo to restore.' }),
            'warning',
            5000,
            {
                iconClass: 'fa-solid fa-rotate-left',
                onClick: () => {

                    // Use the captured data directly instead of relying on state
                    if (undoTimerRef.current) {

                        clearTimeout(undoTimerRef.current);
                        undoTimerRef.current = null;

                    }

                    const { error: deletedRowError, index: deletedIndex, initiallyErrored: initErr, row: deletedRowData } = capturedDeletedData;

                    // Reinsert row at original position
                    setState(currentState => {

                        const newRows = [...currentState.rows];
                        newRows.splice(deletedIndex, 0, deletedRowData);
                        return { rows: newRows };

                    });

                    // Shift and restore errors
                    setState(currentState => {

                        const newErrors: ErrorMap = {};
                        Object.entries(currentState.errors).forEach(([key, val]) => {

                            const idx = Number(key);
                            const newIdx = idx >= deletedIndex ? idx + 1 : idx;
                            newErrors[newIdx] = val;

                        });
                        if (deletedRowError) newErrors[deletedIndex] = deletedRowError;
                        return { errors: newErrors };

                    });

                    // Shift and restore initially errored map
                    setState(currentState => {

                        const newInitiallyErrored: { [index: number]: boolean } = {};
                        Object.entries(currentState.initiallyErrored).forEach(([key, val]) => {

                            const idx = Number(key);
                            const newIdx = idx >= deletedIndex ? idx + 1 : idx;
                            newInitiallyErrored[newIdx] = val;

                        });
                        if (initErr) newInitiallyErrored[deletedIndex] = true;
                        return { initiallyErrored: newInitiallyErrored };

                    });

                    setState({ lastDeletedRow: null });
                    if (isScoped) setState(prev => ({ deletedRowCount: Math.max(0, prev.deletedRowCount - 1) }));

                    // Show success toast
                    createToast(
                        formatMessage({ id: 'row_restored_successfully.1965f810', defaultMessage: 'Row restored successfully.' }),
                        'success'
                    );

                },
                text: formatMessage({ id: 'undo.e1087905', defaultMessage: 'Undo' }),
                variant: 'outline-primary'
            }
        );

    };

    const undoDelete = () => {

        if (!state.lastDeletedRow) return;
        if (undoTimerRef.current) {

            clearTimeout(undoTimerRef.current);
            undoTimerRef.current = null;

        }

        const { error, index, initiallyErrored: initErr, row } = state.lastDeletedRow;

        // Reinsert row at original position
        const newRows = [...state.rows];
        newRows.splice(index, 0, row);

        // Shift and restore errors
        const newErrors: ErrorMap = {};
        Object.entries(state.errors).forEach(([key, val]) => {

            const idx = Number(key);
            const newIdx = idx >= index ? idx + 1 : idx;
            newErrors[newIdx] = val;

        });
        if (error) newErrors[index] = error;

        // Shift and restore initially errored map
        const newInitiallyErrored: { [index: number]: boolean } = {};
        Object.entries(state.initiallyErrored).forEach(([key, val]) => {

            const idx = Number(key);
            const newIdx = idx >= index ? idx + 1 : idx;
            newInitiallyErrored[newIdx] = val;

        });
        if (initErr) newInitiallyErrored[index] = true;

        setState({ errors: newErrors, initiallyErrored: newInitiallyErrored, lastDeletedRow: null, rows: newRows });
        if (isScoped) setState(prev => ({ deletedRowCount: Math.max(0, prev.deletedRowCount - 1) }));

        // Clear any existing toasts and show success toast
        clearAllToasts();
        createToast(
            formatMessage({ id: 'row_restored_successfully.1965f810', defaultMessage: 'Row restored successfully.' }),
            'success'
        );

    };
    const { formatMessage, locale } = useIntl();
    const { catchWithToast, clearAllToasts, createToast } = useNotification();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const gmetrixBannerRef = useRef<any>(null);

    // Resolves a validated access code string to its owning transaction id. Populated during
    // validate() from the on-demand ValidateAccessCodes call and read at submit time so students
    // are added to the correct transaction without loading every access code up front.
    const codeToTransactionRef = useRef<Map<string, number>>(new Map());
    const codesLoadedRef = useRef<Promise<void> | null>(null);

    type UsernameStatus = 'found' | 'notFound' | 'taken';
    type ErrorMap = {
        [rowIndex: number]: { [key: string]: string };
    };

    // At the top of your component
    type CodeStatus = 'valid' | 'invalid' | 'exhausted';

    interface CodeValidationResult {
        status: CodeStatus;
        type?: 'classroom' | 'accessCode';
    }
    interface CodeAssignment {
        codes: { code: string; type: 'classroom' | 'access' }[];
        username: string;
    }
    type FoundStudentData = {
        email: string;
        firstName: string;
        lastName: string;
        username: string;

        // ...any other fields the API returns
    };

    // store validated code assignments
    const validatedCodeAssignments = new Map<string, CodeAssignment>();
    const foundStudentsMap = new Map<string, FoundStudentData>();
    const codeValidationCache = new Map<string, CodeValidationResult>();
    const usernameValidationCache = new Map<string, UsernameStatus>();
    let inFlightPromise: Promise<void> | null = null;
    const classroomCodeToId = new Map<string, number>();
    state.allClassrooms.forEach(c => {

        if (c.Control) classroomCodeToId.set(c.Control.toLowerCase(), c.Id);

    });
    const accessCodeToId = new Map<string, number>();
    state.allAccessCodes.forEach(a => {

        if (a.Codes && a.Codes)
            accessCodeToId.set(a.Codes.toLowerCase(), a.Id);

    });
    const duplicateMappedColumns = useMemo(() => {

        const values = Object.values(state.mapping).filter(Boolean);
        return values.filter((v, i) => values.indexOf(v) !== i);

    }, [state.mapping]);

    const erroredMapColumns = useMemo(() => {

        if (state.step === 2) {

            return matchRequiredFields
                .filter(field => {

                    const value = state.mapping[field.key];
                    return value === undefined || value === null || value === '';

                })
                .map(field => field.label);

        } else {

            return [];

        }

    }, [state.mapping, state.step]);

    const mappedPreviewRows = useMemo(() => state.csvData.slice(0, 5).map(row => {

        const previewRow: StudentRow = {};
        activeFields.forEach(f => {

            previewRow[f.key] = state.mapping[f.key] ? row[state.mapping[f.key]] || '' : '';

        });

        // Show URL code in preview: append to existing or fill empty (only in default context)
        if (urlCode && !isScoped) {

            previewRow.code = previewRow.code ? `${previewRow.code}, ${urlCode}` : urlCode;

        }

        return previewRow;

    }), [state.csvData, state.mapping, urlCode, isScoped]);

    // Only include rows that were initially errored (set during validation)
    const erroredRowIndexes = Object.entries(state.initiallyErrored)
        .filter(([_, val]) => !!val)
        .map(([index]) => Number(index));

    const displayedRows = useMemo(() => {

        if (!state.showOnlyErrors) return state.rows.map((r, i) => ({ index: i, row: r }));

        return erroredRowIndexes
            .filter(i => state.rows[i])
            .map(i => ({ index: i, row: state.rows[i] }));

    }, [state.rows, erroredRowIndexes, state.showOnlyErrors]);

    useEffect(() => {

        if (duplicateMappedColumns.length > 1) {

            createToast(
                formatMessage({ id: 'warning_multiple_fields_are_mapped_to_th.61b8b21f', defaultMessage: 'Warning: Multiple fields are mapped to the same CSV column.' }),
                'warning'
            );

        }

    }, [duplicateMappedColumns]);

    useEffect(() => {

        if (erroredMapColumns.length > 0 && state.step === 2) {

            createToast(
                formatMessage({ id: 'error_some_required_fields_are_not_fille.c5399437', defaultMessage: 'Error: Some required fields are not filled out.' }),
                'danger'
            );

        }

    }, [erroredMapColumns]);

    useEffect(() => {

        codesLoadedRef.current = getAllCodes();

    }, []);

    useEffect(() => () => {

        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);

    }, []);

    const handleFileUpload = (file: File) => {

        if (!file.name.endsWith('.csv')) {

            alert(formatMessage({ id: 'please_upload_a_csv_file.e4bd60e2', defaultMessage: 'Please upload a CSV file' }));
            return;

        }
        Papa.parse(file, {
            complete: res => {

                const parsedHeaders: string[] = res.meta.fields || [];
                const autoMapping: { [key: string]: string } = {};
                ALL_FIELDS.forEach(f => {

                    const match = parsedHeaders.find(
                        h => h.trim().toLowerCase() === f.label.toLowerCase()
                    );
                    if (match) autoMapping[f.key] = match;

                });
                setState({ csvData: res.data as StudentRow[], headers: parsedHeaders, mapping: autoMapping });

            },
            header: true,
            skipEmptyLines: true
        });

    };

    const applyMapping = () => {

        const mapped: StudentRow[] = state.csvData.map(row => {

            const newRow: StudentRow = {};
            activeFields.forEach(f => {

                newRow[f.key] = state.mapping[f.key] ? row[state.mapping[f.key]] || '' : '';

            });

            // Append URL code to existing codes, or fill empty (only in default context)
            if (urlCode && !isScoped) {

                newRow.code = newRow.code ? `${newRow.code}, ${urlCode}` : urlCode;

            }

            return newRow;

        });
        setState({ rows: mapped });
        validate(mapped, true);

    };

    const chunkArray = <T, >(arr: T[], size: number): T[][] => {

        const chunks: T[][] = [];
        for (let i = 0; i < arr.length; i += size) {

            chunks.push(arr.slice(i, i + size));

        }
        return chunks;

    };

    const validateUsernamesOptimized = async (
        usernames: string[]
    ): Promise<void> => {

        const normalized = Array.from(
            new Set(usernames.map(u => u.trim().toLowerCase()))
        );

        // Only validate usernames we haven't seen before
        const toValidate = normalized.filter(
            u => !usernameValidationCache.has(u)
        );

        if (toValidate.length === 0) return;

        // Prevent parallel duplicate requests
        if (inFlightPromise) {

            await inFlightPromise;
            return;

        }

        inFlightPromise = (async () => {

            const chunks = chunkArray(toValidate, 30);

            for (const chunk of chunks) {

                const {
                    foundStudents,
                    notFoundStudents,
                    userNameTakenStudents
                } = await StudentApi.GetBulkStudentsByUsername(chunk);

                foundStudents.forEach((u: Student) => {

                    usernameValidationCache.set(u.Username.toLowerCase(), 'found');
                    foundStudentsMap.set(u.Username.toLowerCase(), u as any); // <-- store full API data

                }

                );
                notFoundStudents.forEach((u: string) => usernameValidationCache.set(u.toLowerCase(), 'notFound')
                );
                userNameTakenStudents.forEach((u: string) => usernameValidationCache.set(u.toLowerCase(), 'taken')
                );

            }

        })();

        await inFlightPromise;
        inFlightPromise = null;

    };

    const getAllCodes = async () => {

        const classrooms = await StudentGroupApi.GetAll('TestingCenter').catch(catchWithToast(formatMessage({ id: 'failed_to_load_classrooms.afc12d1d', defaultMessage: 'Failed to load classrooms.' })));
        const accessCodes = await TransactionApi.GetAll().catch(catchWithToast(formatMessage({ id: 'failed_to_load_access_codes.242fe9e5', defaultMessage: 'Failed to load access codes.' })));
        if (classrooms) setState({ allClassrooms: classrooms });
        if (accessCodes) setState({ allAccessCodes: accessCodes });

    };

    const validate = async (data: StudentRow[], runApiChecks: boolean): Promise<boolean> => {

        const newErrors: ErrorMap = {};
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
        const usernameSet = new Set<string>();

        // LOCAL VALIDATION
        const usernameCounts = new Map<string, number>();
        data.forEach(row => {

            const u = row.username?.trim().toLowerCase();
            if (u) usernameCounts.set(u, (usernameCounts.get(u) || 0) + 1);

        });

        data.forEach((row, i) => {

            const rowErrors: Partial<Record<any, string>> = {};
            const username = row.username?.trim().toLowerCase();

            if (isScoped) {

                // In scoped context, only validate username locally
                // Other field validation is deferred until we know if the student exists
                if (!row.username) rowErrors.username = 'Username is required';
                else if (usernameSet.has(row.username)) rowErrors.username = 'Usernames must be unique';
                if (username && usernameCounts.get(username)! > 1) {

                    rowErrors.username = 'Usernames must be unique';

                }

            } else {

                // Default context: validate all fields
                if (!row.firstName) rowErrors.firstName = 'First name is required';
                if (!row.lastName) rowErrors.lastName = 'Last name is required';
                if (!row.username) rowErrors.username = 'Username is required';
                else if (usernameSet.has(row.username)) rowErrors.username = 'Usernames must be unique';
                const pwErr = validatePassword(row.password);
                if (pwErr) rowErrors.password = pwErr;
                if (!row.email || !emailRegex.test(row.email)) rowErrors.email = 'Email must be valid';

                if (username && usernameCounts.get(username)! > 1) {

                    rowErrors.username = 'Usernames must be unique';

                }

            }

            if (Object.keys(rowErrors).length > 0) newErrors[i] = rowErrors as any;
            usernameSet.add(row.username);

        });

        // Skip API checks if requested
        if (!runApiChecks) {

            setState({ errors: newErrors });

            // mark only rows with actual errors as initially errored (don't clear existing flags)
            setState(prev => {

                const next = { ...prev.initiallyErrored };
                Object.keys(newErrors).forEach(k => {

                    const idx = Number(k);
                    if (Object.keys(newErrors[idx] || {}).length > 0 && !next[idx]) {

                        next[idx] = true;

                    }

                });
                return { initiallyErrored: next };

            });
            return Object.keys(newErrors).length === 0;

        }

        // API / code validations (loading)
        setState({ loading: true });
        try {

            // Wait for the base classroom/access-code lists to load before validating.
            await codesLoadedRef.current;

            // Resolve ONLY the distinct codes present in the import file (not every code the user has
            // access to) in a single request. Covers single-use codes (many unique codes, one use
            // each) as well as multi-use codes, returning total remaining uses and owning transaction.
            const fileCodes = Array.from(new Set(
                data.flatMap(r => (r.code ? r.code.split(',').map(c => c.trim().toLowerCase()) : []))
                    .filter(Boolean)
            ));
            const codeInfoMap = new Map<string, { found: boolean; remaining: number; transactionId: number }>();
            if (!isScoped && fileCodes.length > 0) {

                const validationResults = await TransactionApi.ValidateAccessCodes(fileCodes)
                    .catch(() => [] as AccessCodeValidationResult[]);
                (validationResults || []).forEach(r => {

                    const key = r.Code.toLowerCase();
                    codeInfoMap.set(key, { found: r.Found, remaining: r.Remaining, transactionId: r.TransactionId });
                    if (r.Found && r.TransactionId > 0) codeToTransactionRef.current.set(key, r.TransactionId);

                });

            }

            // Validate usernames via your cached logic
            const usernames = data.map(r => r.username?.trim().toLowerCase()).filter(Boolean) as string[];
            await validateUsernamesOptimized(usernames);

            // Code validation only in default (non-scoped) context
            if (!isScoped) {

                // Track access code and classroom usage across all students
                const accessCodeUsageMap = new Map<string, number>();
                const classroomUsageMap = new Map<string, number>();

                // First pass: count how many students want each access code and classroom
                data.forEach(row => {

                    if (row.code) {

                        const codes = row.code.split(',').map(c => c.trim().toLowerCase()).filter(Boolean);
                        codes.forEach(c => {

                            const accessMatch = state.allAccessCodes.find(ac => ac.Codes?.toLowerCase() === c);
                            const classroomMatch = state.allClassrooms.find(cl => cl.Control?.toLowerCase() === c);
                            const codeInfo = codeInfoMap.get(c);

                            if (accessMatch && !accessMatch.Archived && accessMatch.UsesRemaining && accessMatch.UsesRemaining > 0) {

                                accessCodeUsageMap.set(c, (accessCodeUsageMap.get(c) || 0) + 1);

                            } else if (codeInfo && codeInfo.found && codeInfo.remaining > 0) {

                                accessCodeUsageMap.set(c, (accessCodeUsageMap.get(c) || 0) + 1);

                            }
                            if (classroomMatch) {

                                classroomUsageMap.set(c, (classroomUsageMap.get(c) || 0) + 1);

                            }

                        });

                    }

                });

                // Validate codes (multiple, comma-separated) against allClassrooms and allAccessCodes
                data.forEach((row, i) => {

                    const username = row.username?.trim().toLowerCase();
                    if (!username) return;

                    const rowCodes: { code: string; type: 'classroom' | 'access' }[] = [];

                    if (row.code) {

                        const codes = row.code.split(',').map(c => c.trim()).filter(Boolean);

                        codes.forEach(c => {

                            // Check classroom
                            let errorMessage = `Invalid code: ${c}`;
                            let classroomMatch = state.allClassrooms.find(cl => cl.Control?.toLowerCase() === c.toLowerCase());
                            if (classroomMatch) {

                                // Check if classroom is archived
                                if (classroomMatch.Archived) {

                                    errorMessage = `Inactive classroom: ${c}`;
                                    classroomMatch = undefined;

                                } else {

                                    // Validate classroom capacity if it has a MaxSize limit
                                    const codeKey = c.toLowerCase();
                                    const requestedUses = classroomUsageMap.get(codeKey) || 0;
                                    if (classroomMatch.MaxStudents && requestedUses > classroomMatch.MaxStudents) {

                                        // Classroom doesn't have capacity for all students
                                        errorMessage = `Not enough capacity in classroom: ${c} (${classroomMatch.MaxStudents} capacity, ${requestedUses} requested)`;
                                        classroomMatch = undefined;

                                    } else {

                                        rowCodes.push({ code: c, type: 'classroom' });

                                    }

                                }

                            }

                            // Check access codes
                            let accessMatch = state.allAccessCodes.find(ac => ac.Codes?.toLowerCase() === c.toLowerCase());

                            // Checks if access code is available, active, and has uses remaining
                            if (accessMatch && accessMatch.UsesRemaining && !accessMatch.Archived && accessMatch.UsesRemaining > 0) {

                                const codeKey = c.toLowerCase();
                                const requestedUses = accessCodeUsageMap.get(codeKey) || 0;

                                // Check if this specific code has enough uses for all students requesting it
                                if (requestedUses > accessMatch.UsesRemaining) {

                                    // This student exceeds the available uses
                                    errorMessage = `Not enough uses remaining on code: ${c} (${accessMatch.UsesRemaining || 0} available, ${requestedUses} requested)`;
                                    accessMatch = undefined;

                                } else {

                                    rowCodes.push({ code: c, type: 'access' });

                                }

                            } else if (accessMatch && accessMatch.Archived) {

                                accessMatch = undefined;
                                errorMessage = `Inactive code: ${c}`;

                            } else if (accessMatch && accessMatch.UsesRemaining && accessMatch.UsesRemaining === 0) {

                                accessMatch = undefined;
                                errorMessage = `Not enough uses on this code: ${c}`;

                            }

                            // Check individual access codes resolved on demand (single-use codes,
                            // which are not exposed on Transaction.Codes, as well as any other code
                            // not matched above). Uses the file-scoped ValidateAccessCodes results.
                            let singleUseValid = false;
                            if (!classroomMatch && !accessMatch) {

                                const codeInfo = codeInfoMap.get(c.toLowerCase());
                                if (codeInfo && codeInfo.found) {

                                    if (codeInfo.remaining <= 0) {

                                        errorMessage = `Code has no remaining uses: ${c}`;

                                    } else {

                                        const codeKey = c.toLowerCase();
                                        const requestedUses = accessCodeUsageMap.get(codeKey) || 0;
                                        if (requestedUses > codeInfo.remaining) {

                                            errorMessage = `Not enough uses remaining on code: ${c} (${codeInfo.remaining} available, ${requestedUses} requested)`;

                                        } else {

                                            rowCodes.push({ code: c, type: 'access' });
                                            singleUseValid = true;

                                        }

                                    }

                                }

                            }

                            // If not found in either, mark as invalid
                            if (!classroomMatch && !accessMatch && !singleUseValid) {

                                newErrors[i] ??= {};
                                newErrors[i].code = errorMessage;

                            }

                        });

                    }

                    // Save valid assignments to the Map
                    if (rowCodes.length > 0) {

                        validatedCodeAssignments.set(username, {
                            codes: rowCodes,
                            username
                        });

                    }

                });

            } // end if (!isScoped)

            // Apply username API rules
            data.forEach((row, i) => {

                const username = row.username?.trim().toLowerCase();
                if (!username) return;

                const status = usernameValidationCache.get(username);
                if (!status) return;

                newErrors[i] ??= {};

                if (isScoped) {

                    // In scoped context: 'found' students are OK (will be attached), 'taken' is an error
                    if (status === 'taken') newErrors[i].username = 'Username already used elsewhere in the system. Choose another username.';

                    // For 'notFound' (new students), validate required fields
                    if (status === 'notFound') {

                        if (!row.firstName) newErrors[i].firstName = 'First name is required';
                        if (!row.lastName) newErrors[i].lastName = 'Last name is required';
                        const pwErrScoped = validatePassword(row.password);
                        if (pwErrScoped) newErrors[i].password = pwErrScoped;

                    }

                    // For 'found', clear any non-username errors (existing student needs no fields)
                    if (status === 'found') {

                        delete newErrors[i];

                    }

                } else {

                    if (status === 'taken') newErrors[i].username = 'Username already used elsewhere in the system. Choose another username.';
                    if (status === 'found' && !state.updateExisting) newErrors[i].username = 'Username already exists';

                }

                // Clean up empty error objects
                if (newErrors[i] && Object.keys(newErrors[i]).length === 0) delete newErrors[i];

            });

        } finally {

            setState({ loading: false });

        }

        setState({ errors: newErrors });

        // mark only rows with actual errors as initially errored (don't clear existing flags)
        setState(prev => {

            const next = { ...prev.initiallyErrored };
            Object.keys(newErrors).forEach(k => {

                const idx = Number(k);
                if (Object.keys(newErrors[idx] || {}).length > 0 && !next[idx]) {

                    next[idx] = true;

                }

            });
            return { initiallyErrored: next };

        });

        // ? return false if any row has a non-empty error object
        const hasErrors = Object.values(newErrors).some(obj => Object.keys(obj).length > 0);
        return !hasErrors;

    };

    const updateCell = (rowIndex: number, key: string, value: string, forceValidate = false) => {

        const updatedRows = [...state.rows];
        updatedRows[rowIndex][key] = value;
        setState({ rows: updatedRows });
        const displayedIndex = displayedRows.findIndex(d => d.index === rowIndex);
        if (displayedIndex !== -1 && state.lockedErrorIndexes.includes(displayedIndex) && !forceValidate) {

            return;

        }

        // Only validate this cell
        setState(prevState => {

            const prevErrors = prevState.errors;
            const newErrors = { ...prevErrors };
            const rowErrors = newErrors[rowIndex] ? { ...newErrors[rowIndex] } : {};

            if (isScoped) {

                // In scoped context, validation depends on whether student exists
                if (key === 'username') {

                    const trimmed = value.trim().toLowerCase();
                    if (!value) rowErrors.username = 'Username is required';
                    else if (rows.some((r, i) => i !== rowIndex && r.username.trim().toLowerCase() === trimmed))
                        rowErrors.username = 'Usernames must be unique';
                    else delete rowErrors.username;

                    const status = usernameValidationCache.get(trimmed);
                    if (status === 'taken') rowErrors.username = 'Username already used elsewhere in the system. Choose another username.';

                    // If found (existing student), clear field errors
                    if (status === 'found') {

                        delete rowErrors.firstName;
                        delete rowErrors.lastName;
                        delete rowErrors.password;
                        delete rowErrors.email;

                    }

                    // If notFound (new student), apply required field validation
                    if (status === 'notFound') {

                        const currentRow = updatedRows[rowIndex];
                        if (!currentRow.firstName) rowErrors.firstName = 'First name is required';
                        else delete rowErrors.firstName;
                        if (!currentRow.lastName) rowErrors.lastName = 'Last name is required';
                        else delete rowErrors.lastName;
                        const pwErrCurrent = validatePassword(currentRow.password);
                        if (pwErrCurrent) rowErrors.password = pwErrCurrent;
                        else delete rowErrors.password;

                    }

                } else {

                    // For non-username fields, only validate if student is new (notFound)
                    const usernameVal = updatedRows[rowIndex]?.username?.trim().toLowerCase();
                    const status = usernameValidationCache.get(usernameVal);

                    if (status === 'notFound') {

                        if (key === 'firstName' && !value) rowErrors.firstName = 'First name is required';
                        else if (key === 'firstName') delete rowErrors.firstName;

                        if (key === 'lastName' && !value) rowErrors.lastName = 'Last name is required';
                        else if (key === 'lastName') delete rowErrors.lastName;

                        if (key === 'password') {

                            const pwErrKey = validatePassword(value);
                            if (pwErrKey) rowErrors.password = pwErrKey;
                            else delete rowErrors.password;

                        }

                    }

                    // Email is optional in scoped context � no validation needed

                }

            } else {

                // Local validation rules
                if (key === 'firstName' && !value) rowErrors.firstName = 'First name is required';
                else if (key === 'firstName') delete rowErrors.firstName;

                if (key === 'lastName' && !value) rowErrors.lastName = 'Last name is required';
                else if (key === 'lastName') delete rowErrors.lastName;

                if (key === 'username') {

                    const trimmed = value.trim().toLowerCase();
                    if (!value) rowErrors.username = 'Username is required';
                    else if (state.rows.some((r, i) => i !== rowIndex && r.username.trim().toLowerCase() === trimmed))
                        rowErrors.username = 'Usernames must be unique';
                    else delete rowErrors.username;

                    // Apply cached username API checks if needed
                    const status = usernameValidationCache.get(trimmed);
                    if (status === 'taken') rowErrors.username = 'Username already exists';
                    if (status === 'found' && !state.updateExisting) rowErrors.username = 'Username already exists';

                }

                if (key === 'password') {

                    const pwErrNonScoped = validatePassword(value);
                    if (pwErrNonScoped) rowErrors.password = pwErrNonScoped;
                    else delete rowErrors.password;

                }

                if (key === 'email') {

                    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;
                    if (!value || !emailRegex.test(value)) rowErrors.email = 'Email must be valid';
                    else delete rowErrors.email;

                }

                if (key === 'code') {

                    const codes = value.split(',').map(c => c.trim().toLowerCase()).filter(Boolean);
                    let codeError: string | undefined;
                    codes.forEach(c => {

                        const status = codeValidationCache.get(c);
                        if (!c) return; // skip empty
                        if (status?.status === 'invalid') codeError = 'Invalid code';
                        if (status?.status === 'exhausted') codeError = 'Code has no remaining uses';

                    });
                    if (codeError) rowErrors.code = codeError;
                    else delete rowErrors.code;

                }

            } // end else (non-scoped)

            if (Object.keys(rowErrors).length > 0) newErrors[rowIndex] = rowErrors;
            else delete newErrors[rowIndex];

            return { errors: newErrors };

        });

    };

    const importStudents = async () => {

        setState({ initiallyErrored: {}, lockedErrorIndexes: [] });

        const isValid = await validate(state.rows, true);
        if (!isValid) {

            const errorOrigIndexes = Object.keys(state.errors).map(Number);
            const locked = errorOrigIndexes.map(orig => displayedRows.findIndex(d => d.index === orig)).filter(i => i >= 0);
            setState({ lockedErrorIndexes: locked });
            return;

        }

        setState({ loading: true });
        try {

            if (isScoped) {

                // System mode: associate created students with the target record's OWNING testing
                // center (resolved from the already-loaded code lists), not the left-menu Selected /
                // System partner. Falls back to the selected partner when the target isn't listed.
                const scopedPartnerId = urlContext === 'group'
                    ? (state.allClassrooms.find(c => c.Id === Number(urlGroupId))?.Partner?.Id ?? selectedPartner.id)
                    : (state.allAccessCodes.find(a => a.Id === Number(urlAccessCodeId))?.PartnerId ?? selectedPartner.id);

                // Scoped import: create new students, attach all to current context
                const createdStudents: { studentId: number; username: string }[] = [];
                const addedStudents: { studentId: number; username: string }[] = [];
                let failedCount = 0;

                for (const student of state.rows) {

                    const username = student.username?.trim().toLowerCase();
                    const status = usernameValidationCache.get(username!);

                    try {

                        if (status === 'notFound') {

                            const newStudentPayload = {
                                Country: { Id: 0, Name: 'None' },
                                Email: student.email || '',
                                Firstname: student.firstName,
                                Groups: [],
                                IsAda: false,
                                Lastname: student.lastName,
                                Password: student.password,
                                Role: 'Student',
                                SelectedPartner: { Name: '', PartnerId: scopedPartnerId, Type: 0 },
                                State: { Id: 0, Name: 'None' },
                                StudentNumber: student.studentNumber || '',
                                Username: student.username
                            };
                            const created = await StudentApi.Create(newStudentPayload as any);
                            createdStudents.push({ studentId: created.Id, username });

                        } else if (status === 'found') {

                            const apiData: any = foundStudentsMap.get(username!);
                            if (apiData) {

                                addedStudents.push({ studentId: apiData.Id, username });

                            }

                        }

                    } catch {

                        failedCount++;

                    }

                }

                // Attach all students to the current context
                const allStudentIds = [...createdStudents, ...addedStudents].map(s => s.studentId);

                if (urlContext === 'group' && urlGroupId) {

                    const groupId = Number(urlGroupId);
                    if (allStudentIds.length > 0) {

                        await StudentGroupApi.AddStudentToGroup([groupId], allStudentIds);
                        const txs = await StudentGroupApi.GetTransactionsFromGroup(groupId, scopedPartnerId).catch(() => []);
                        if (txs && txs.length > 0) {

                            const txIds = txs.map(t => t.Id);
                            await TransactionApi.AddStudentToTransaction(txIds, allStudentIds, scopedPartnerId);

                        }

                    }

                } else if (urlContext === 'accessCodes' && urlAccessCodeId) {

                    const accessCodeId = Number(urlAccessCodeId);
                    if (allStudentIds.length > 0) {

                        await TransactionApi.AddStudentToTransaction([accessCodeId], allStudentIds, scopedPartnerId);

                    }

                }

                setState({
                    result: {
                        added: addedStudents.length,
                        created: createdStudents.length,
                        skipped: state.deletedRowCount + failedCount,
                        updated: 0
                    },
                    step: 4
                });

            } else {

                const createdStudents: { studentId: number | undefined; username: string }[] = [];
                const updatedStudents: { studentId: number; username: string }[] = [];

                // 1?? Create new students
                for (const student of state.rows) {

                    const username = student.username?.trim().toLowerCase();
                    const status = usernameValidationCache.get(username!);

                    if (status === 'notFound') {

                        const newStudentPayload = {
                            Country: { Id: 0, Name: 'None' },
                            Email: student.email,
                            Firstname: student.firstName,
                            Groups: [],
                            IsAda: false,
                            Lastname: student.lastName,
                            Password: student.password,
                            Role: 'Student',
                            SelectedPartner: { Name: '', PartnerId: selectedPartner.id, Type: 0 },
                            State: { Id: 0, Name: 'None' },
                            StudentNumber: student.studentNumber,
                            Username: student.username
                        };

                        const created = await StudentApi.Create(newStudentPayload as any);
                        createdStudents.push({ studentId: created.Id, username });

                    } else if (status === 'found' && state.updateExisting) {

                        const apiData: any = foundStudentsMap.get(username!);
                        if (apiData) {

                            const updatePayload = {
                                ...apiData,
                                Country: apiData.Country ?? { Id: 0, Name: 'None' },
                                Email: student.email,
                                Firstname: student.firstName,
                                Groups: [],
                                IsAda: apiData.IsAda ?? false,
                                Lastname: student.lastName,
                                Password: student.password,
                                Role: 'Student',
                                SelectedPartner: apiData.SelectedPartner ?? { Name: '', PartnerId: selectedPartner.id, Type: 0 },
                                State: apiData.State ?? { Id: 0, Name: 'None' },
                                StudentNumber: student.studentNumber !== '' ? student.studentNumber : apiData.StudentNumber
                            };
                            await StudentApi.Update(updatePayload as any);
                            updatedStudents.push({ studentId: apiData.Id, username });

                        }

                    }

                }

                // 2?? Build a map of all students with IDs
                const allStudentsMap = new Map<string, number>();
                [...createdStudents, ...updatedStudents].forEach((s: any) => allStudentsMap.set(s.username, s.studentId));

                // 3?? Assign codes
                // Collect all unique classroom IDs needed across all assignments
                const allNeededClassroomIds = new Set<number>();
                for (const [, assignment] of validatedCodeAssignments.entries()) {

                    assignment.codes
                        .filter(c => c.type === 'classroom')
                        .forEach(c => {

                            const id = classroomCodeToId.get(c.code.toLowerCase());
                            if (id) allNeededClassroomIds.add(id);

                        });

                }

                // Fetch transaction IDs only for the classrooms being used
                const txMap = new Map<number, number[]>();
                await Promise.all(Array.from(allNeededClassroomIds).map(async classroomId => {

                    const txs = await StudentGroupApi.GetTransactionsFromGroup(classroomId).catch(() => []);
                    if (txs && txs.length > 0) {

                        txMap.set(classroomId, txs.map(t => t.Id));

                    }

                }));

                for (const [username, assignment] of validatedCodeAssignments.entries()) {

                    const studentId = allStudentsMap.get(username);
                    if (!studentId) continue; // safety check
                    // Classroom IDs
                    const classroomIds: number[] = assignment.codes
                        .filter(c => c.type === 'classroom')
                        .map(c => classroomCodeToId.get(c.code.toLowerCase()))
                        .filter(Boolean) as number[];

                    if (classroomIds.length > 0) {

                        await StudentGroupApi.AddStudentToGroup(classroomIds, [studentId]);

                        // Also add student to the transactions associated with these classrooms
                        const classroomTxIds = classroomIds.flatMap(id => txMap.get(id) || []);
                        if (classroomTxIds.length > 0) {

                            await TransactionApi.AddStudentToTransaction(classroomTxIds, [studentId]);

                        }

                    }

                    // Access code IDs
                    const accessIds: number[] = assignment.codes
                        .filter(c => c.type === 'access')
                        .map(c => accessCodeToId.get(c.code.toLowerCase()) ?? codeToTransactionRef.current.get(c.code.toLowerCase()))
                        .filter(Boolean) as number[];
                    if (accessIds.length > 0) {

                        await TransactionApi.AddStudentToTransaction(accessIds, [studentId]);

                    }

                }

                setState({
                    result: {
                        added: 0,
                        created: createdStudents.length,
                        skipped: 0,
                        updated: updatedStudents.length
                    },
                    step: 4
                });

            } // end else (non-scoped)

        } finally {

            setState({ loading: false });

        }

    };

    const handleStepChange = (e: { value: number }) => {

        // Only allow navigating back to previously completed steps; forward
        // navigation stays gated behind the Next / action buttons so validation runs.
        const target = e.value + 1;
        if (target < state.step) setState({ showOnlyErrors: false, step: target });

    };

    return (
        <DefaultLayout pageTitle={formatMessage({ id: 'import_students.f041ccf5', defaultMessage: 'Import Students' })}>
            <KendoIntlProvider>
                <LocalizationProvider language={locale}>
                    <IntlProvider locale={locale}>
                        <>
                            <PageHeader helpContent={helpContent} title={formatMessage({ id: 'import_students.f041ccf5', defaultMessage: 'Import Students' })} />
                            <div className="container mt-4">
                                <div className="card shadow-lg p-4">
                                    <GMetrixStepper
                                        linear
                                        handleChange={handleStepChange}
                                        setStep={value => setState({ step: (typeof value === 'function' ? value(state.step - 1) : value) + 1 })}
                                        step={state.step - 1}
                                        stepItems={steps.map(label => ({ label }))}
                                    />
                                    <GMetrixBanner ref={gmetrixBannerRef} />
                                    {state.loading && (
                                    <div
                                        aria-live="assertive"
                                        className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-white bg-opacity-75"
                                        style={{ zIndex: 10 }}
                                    >
                                        {/* Spinner */}
                                        <div className="text-center mb-3">
                                            <div className="spinner-border text-primary mb-2" />
                                            <div className="fw-bold">
                                                <FormattedMessage defaultMessage="Validating data" id="validating_data.2c952eca" />
                                            </div>
                                        </div>

                                        {/* Play Game Button */}
                                        {!state.showGame && (
                                        <button
                                            className="btn btn-outline-primary mt-2"
                                            onClick={() => setState({ showGame: true })}
                                            type="button"
                                        >
                                            <FormattedMessage defaultMessage="Play a game while you wait" id="play_a_game_while_you_wait.d61188b7" />
                                        </button>
                                        )}

                                        {/* Tic-Tac-Toe game */}
                                        {state.showGame && (
                                        <div style={{ marginTop: 16 }}>
                                            <TicTacToe />
                                        </div>
                                        )}
                                    </div>
                                    )}

                                    {state.step === 1 && (
                                    <div>
                                        {isScoped && (
                                        <div className="alert alert-info mt-2 mb-4 d-flex align-items-center" role="alert">
                                            <i className="fa-solid fa-circle-info me-2"></i>
                                            <FormattedMessage
                                                defaultMessage="Students will be imported into the current {context, select, group {classroom} other {access code}}. Existing students will be attached without updating their information." id="students_will_be_imported_into_the_curre.675a37d4"
                                                values={{ context: urlContext }}
                                            />
                                        </div>
                                        )}
                                        <div
                                            className={`border rounded p-4 text-center upload-dropzone ${state.isDragging ? 'bg-primary bg-opacity-10 border-primary' : 'border-secondary'
                                    } ${state.csvData.length > 0 ? 'border-success' : ''}`}
                                            onDragOver={e => {

                                                e.preventDefault();
                                                setState({ isDragging: true });

                                            }}
                                            onDrop={e => {

                                                e.preventDefault();
                                                setState({ isDragging: false });
                                                if (e.dataTransfer.files.length) {

                                                    handleFileUpload(e.dataTransfer.files[0]);

                                                }

                                            }}
                                            onKeyDown={e => {

                                                if (e.key === 'Enter' || e.key === ' ') {

                                                    fileInputRef.current?.click();

                                                }

                                            }}
                                            aria-label={formatMessage({ id: 'upload_csv_file_click_or_drag_and_drop.7eaae336', defaultMessage: 'Upload CSV file. Click or drag and drop.' })}
                                            onClick={() => fileInputRef.current?.click()}
                                            onDragLeave={() => setState({ isDragging: false })}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            <strong>
                                                <FormattedMessage defaultMessage="Drag & drop your CSV here" id="drag_and_drop_your_csv_here.0d8a3bae" />
                                            </strong>
                                            <div className="text-muted">
                                                <FormattedMessage defaultMessage="or click to browse" id="or_click_to_browse.8c860e74" />
                                            </div>
                                            {state.csvData.length > 0 && (
                                            <div className="text-success">
                                                <FormattedMessage defaultMessage="File uploaded successfully" id="file_uploaded_successfully.83e54140" />
                                            </div>
                                            )}
                                        </div>
                                        <input
                                            accept=".csv"
                                            className="d-none"
                                            onChange={e => e.target.files && handleFileUpload(e.target.files[0])}
                                            ref={fileInputRef}
                                            type="file"
                                        />

                                        <a
                                            className="mt-2"
                                            download={'template.pdf'}
                                            href="https://www.gmetrix.net/documentation/Documents/StudentImportTemplate_V2.csv"
                                        >
                                            <FormattedMessage defaultMessage="Download Template" id="download_template.36b13dfc" />
                                        </a>
                                        <GMetrixCheckInput
                                            aria-label={formatMessage({ id: 'update_existing_students.65839762', defaultMessage: 'Update existing students' })}
                                            checked={state.updateExisting}
                                            className={`mt-4 ${isScoped ? 'd-none' : ''}`}
                                            label={formatMessage({ id: 'update_existing_students_that_match_by_u.acea8c31', defaultMessage: 'Update existing students that match by username' })}
                                            onChange={(e: ChangeEvent<HTMLInputElement>) => setState({ updateExisting: e.target.checked })}
                                            tabIndex={0}
                                            type={'checkbox'}
                                        />

                                        <div className="d-flex justify-content-end">
                                            <GMetrixButton
                                                className="mt-4"
                                                disabled={!state.csvData.length}
                                                onClick={() => setState({ step: 2 })}
                                                variant="primary"
                                            >
                                                <FormattedMessage defaultMessage="Next" id="next.50b7834e" />
                                            </GMetrixButton>
                                        </div>
                                    </div>
                                    )}

                                    {state.step === 2 && (
                                    <div className="mt-4">
                                        {urlCode && !isScoped && (
                                        <div className="alert alert-info mb-3 d-flex align-items-center" role="alert">
                                            <i className="fa-solid fa-circle-info me-2"></i>
                                            <FormattedMessage defaultMessage="Code" id="code.cc651d9c" />
                                            {' '}
                                            <strong className="ms-1">
                                                {urlCode}
                                            </strong>
                                            &nbsp;
                                            <FormattedMessage defaultMessage="will be automatically applied to all imported students." id="will_be_automatically_applied_to_all_imp.72e3b612" />
                                        </div>
                                        )}
                                        <div className="table-responsive sticky-table-scroll">
                                            <table className="table table-bordered table-sm sticky-table-header">
                                                <thead>
                                                    <tr>
                                                        {' '}
                                                        {activeFields.map(f => (
                                                            <th key={f.key}>
                                                                <label>
                                                                    {f.label}
                                                                    {' '}
                                                                    {matchRequiredFields.find(r => r.key === f.key) && (
                                                                    <span className="text-danger">
                                                                        *
                                                                    </span>
                                                                    )}
                                                                </label>
                                                                <br />
                                                                <small>
                                                                    <FormattedMessage defaultMessage="mapped from:" id="mapped_from.28e6f30e" />
                                                                </small>
                                                                <select
                                                                    aria-label={formatMessage({ id: 'map_column_for_field.162f02b3', defaultMessage: 'Map column for {field}' }, { field: f.label })}
                                                                    className={`form-select ${matchRequiredFields.find(r => r.key === f.key && state.mapping[f.key] === undefined) && 'border border-danger'}`}
                                                                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setState({ mapping: { ...state.mapping, [f.key]: e.target.value } })}
                                                                    tabIndex={0}
                                                                    value={state.mapping[f.key] || ''}
                                                                >
                                                                    {matchRequiredFields.find(r => r.key === f.key) ? (
                                                                        <option value="">
                                                                            {formatMessage({ id: 'requires_match.b1b6bf8d', defaultMessage: '-- Requires Match --' })}
                                                                        </option>
                                                                    ) : (
                                                                        <option value="">
                                                                            {formatMessage({ id: 'not_included.e1a51394', defaultMessage: '-- Not Included --' })}
                                                                        </option>
                                                                    )}

                                                                    {state.headers.map(h => (
                                                                        <option key={h} value={h}>
                                                                            {h}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {mappedPreviewRows.map((row, i) => (
                                                        <tr key={i}>
                                                            {activeFields.map(f => (
                                                                <td key={f.key}>
                                                                    {row[f.key]}
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                        <div className="d-flex justify-content-between mt-4">
                                            <GMetrixButton
                                                onClick={() => {

                                                    setState({ showOnlyErrors: false, step: 1 });

                                                }} variant="outline-secondary"
                                            >
                                                <FormattedMessage defaultMessage="Back" id="back.b6238c9e" />
                                            </GMetrixButton>
                                            <GMetrixButton
                                                onClick={() => {

                                                    applyMapping(); setState({ step: 3 });

                                                }} disabled={matchRequiredFields.some(f => !state.mapping[f.key])} variant="primary"
                                            >
                                                <FormattedMessage defaultMessage="Next" id="next.50b7834e" />
                                            </GMetrixButton>
                                        </div>
                                    </div>
                                    )}

                                    {state.step === 3 && (
                                    <div>
                                        <div className="d-flex justify-content-between mb-3">
                                            <div>
                                                <div className="d-flex">
                                                    <GMetrixCheckInput
                                                        onChange={(e: any) => {

                                                            const willShowOnlyErrors = e.target.checked;
                                                            setState({ showOnlyErrors: willShowOnlyErrors });
                                                            if (willShowOnlyErrors) {

                                                                setState({ lockedErrorIndexes: Array.from({ length: erroredRowIndexes.length }, (_, i) => i) });

                                                            } else {

                                                                setState({ lockedErrorIndexes: [] });

                                                            }

                                                        }}
                                                        checked={state.showOnlyErrors}
                                                        className="mb-2"
                                                        label={formatMessage({ id: 'show_only_rows_with_errors.069ae79b', defaultMessage: 'Show only rows with errors' })}
                                                        type="switch"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="table-responsive sticky-table-scroll">
                                            <table className="table table-bordered table-sm sticky-table-header">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 40 }}></th>
                                                        {activeFields.map(f => (
                                                            <th key={f.key}>
                                                                {f.label}
                                                                <br></br>
                                                                <small>
                                                                    <FormattedMessage defaultMessage="mapped from:" id="mapped_from.28e6f30e" />
                                                                </small>
                                                                <br></br>
                                                                {state.mapping[f.key]}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {displayedRows.map(({ index: i, row }) => (
                                                        <tr className={Object.keys(state.errors[i] || {}).length > 0 ? 'table-danger' : ''} key={i}>
                                                            <td className="text-center align-middle" style={{ width: 40 }}>
                                                                <i
                                                                    aria-label={formatMessage({ id: 'delete_row.17561c22', defaultMessage: 'Delete row' })}
                                                                    className="fa-regular fa-trash"
                                                                    onClick={() => deleteRow(i)}
                                                                    onMouseEnter={() => setState({ hoveredTrashRow: i })}
                                                                    onMouseLeave={() => setState({ hoveredTrashRow: null })}
                                                                    role="button"
                                                                    style={{ color: state.hoveredTrashRow === i ? '#dc3545' : '#6c757d', cursor: 'pointer' }}
                                                                />
                                                            </td>
                                                            {activeFields.map(f => (
                                                                <OverlayTrigger
                                                                    overlay={state.errors[i]?.[f.key] ? (
                                                                        <Tooltip>
                                                                            {state.errors[i][f.key]}
                                                                        </Tooltip>
                                                                    ) : <></>}
                                                                    key={f.key}
                                                                    placement="top"
                                                                >
                                                                    <td aria-label={state.errors[i]?.[f.key] || formatMessage({ id: 'field_cell.ecff8488', defaultMessage: '{field} cell' }, { field: f.label })} tabIndex={0}>
                                                                        <GMetrixInput
                                                                            onFocus={() => {

                                                                                if (state.showOnlyErrors && state.lockedErrorIndexes.length === 0) {

                                                                                    setState({ lockedErrorIndexes: Array.from({ length: displayedRows.length }, (_, i) => i) });

                                                                                }

                                                                            }}
                                                                            aria-describedby={state.errors[i]?.[f.key] ? `error-${i}-${f.key}` : undefined}
                                                                            aria-invalid={!!state.errors[i]?.[f.key]}
                                                                            className={`form-control form-control-sm ${state.errors[i]?.[f.key] ? 'is-invalid' : ''}`}
                                                                            onChange={(e: ChangeEvent<HTMLInputElement>) => updateCell(i, f.key, e.target.value, true)}
                                                                            tabIndex={0}
                                                                            value={row[f.key]}
                                                                        />
                                                                    </td>
                                                                </OverlayTrigger>
                                                            ))}
                                                        </tr>
                                                    ))}
                                                </tbody>

                                            </table>
                                        </div>

                                        <div className="d-flex justify-content-between mt-4">
                                            <GMetrixButton onClick={() => setState({ step: 2 })} variant="secondary">
                                                <FormattedMessage defaultMessage="Back" id="back.b6238c9e" />
                                            </GMetrixButton>
                                            <GMetrixButton
                                                disabled={Object.values(state.errors).some(rowErr => Object.keys(rowErr).length > 0) || state.loading}
                                                onClick={importStudents}
                                                variant="primary"
                                            >
                                                {state.loading ? <FormattedMessage defaultMessage="Importing..." id="importing.ffc41f85" /> : <FormattedMessage defaultMessage="Import Students" id="import_students.f041ccf5" />}
                                            </GMetrixButton>

                                        </div>
                                    </div>
                                    )}

                                    {state.step === 4 && (
                                    <div className="text-center">
                                        <h4 className="text-success">
                                            <FormattedMessage defaultMessage="Import Complete" id="import_complete.fb71078f" />
                                        </h4>
                                        <i className="fal fa-check-circle text-success fa-10x mb-4 mt-4"></i>
                                        <p>
                                            {state.result.created}
                                            {' '}
                                            <FormattedMessage defaultMessage="students created" id="students_created.244aa58a" />
                                        </p>
                                        {isScoped ? (
                                            <>
                                                <p>
                                                    {state.result.added}
                                                    {' '}
                                                    <FormattedMessage defaultMessage="students added" id="students_added.238d7971" />
                                                </p>
                                                <p>
                                                    {state.result.skipped}
                                                    {' '}
                                                    <FormattedMessage defaultMessage="students skipped" id="students_skipped.87d736fc" />
                                                </p>
                                            </>
                                        ) : (
                                            <p>
                                                {state.result.updated}
                                                {' '}
                                                <FormattedMessage defaultMessage="students updated" id="students_updated.3fa9b2a8" />
                                            </p>
                                        )}
                                        <div className="d-flex justify-content-center gap-3 mt-4">
                                            <GMetrixButton onClick={() => window.location.reload()} variant="primary">
                                                <FormattedMessage defaultMessage="Import More" id="import_more.3b91159b" />
                                            </GMetrixButton>
                                            <GMetrixButton
                                                onClick={() => {

                                                    if (urlContext === 'group' && urlGroupId) {

                                                        navigate(`/classrooms/${urlGroupId}`);

                                                    } else if (urlContext === 'accessCodes' && urlAccessCodeId) {

                                                        navigate(`/access-codes/${urlAccessCodeId}`);

                                                    } else {

                                                        navigate('/students');

                                                    }

                                                }}
                                                variant="outline-primary"
                                            >
                                                {urlContext === 'group' ? <FormattedMessage defaultMessage="Back to Classroom" id="back_to_classroom.584d50a0" /> : urlContext === 'accessCodes' ? <FormattedMessage defaultMessage="Back to Access Code" id="back_to_access_code.b65ccb32" /> : <FormattedMessage defaultMessage="Back to Students" id="back_to_students.9cea9aa7" />}
                                            </GMetrixButton>
                                        </div>
                                    </div>
                                    )}
                                </div>
                            </div >
                        </>
                    </IntlProvider>
                </LocalizationProvider>
            </KendoIntlProvider>
        </DefaultLayout>
    );

};

const TicTacToe = () => {

    const { formatMessage } = useIntl();

    const [state, setState] = useSuperState({
        board: Array(9).fill(null) as (string | null)[],
        isThinking: false,
        message: formatMessage({ id: 'your_turn.f302daca', defaultMessage: 'Your turn!' }) as string
    });

    const { board, isThinking, message } = state;

    const checkWinner = (b: (string | null)[]) => {

        const lines = [
            [0, 1, 2], [3, 4, 5], [6, 7, 8],
            [0, 3, 6], [1, 4, 7], [2, 5, 8],
            [0, 4, 8], [2, 4, 6]
        ];
        for (const [a, b1, c] of lines) {

            if (b[a] && b[a] === b[b1] && b[a] === b[c]) return b[a];

        }
        if (b.every(cell => cell)) return 'Tie';
        return null;

    };

    const handleClick = (index: number) => {

        if (board[index] || checkWinner(board) || isThinking) return;

        const newBoard = [...board];
        newBoard[index] = 'X';
        setState({ board: newBoard });

        const winner = checkWinner(newBoard);
        if (winner) {

            setState({ message: winner === 'Tie' ? formatMessage({ id: 'its_a_tie.3670a5e4', defaultMessage: "It's a tie!" }) : formatMessage({ id: 'winner_wins.e72376bf', defaultMessage: '{winner} wins!' }, { winner }) });
            return;

        }

        // Computer move with delay
        setState({ isThinking: true, message: formatMessage({ id: 'computer_is_thinking.ca15570b', defaultMessage: 'Computer is thinking...' }) });
        setTimeout(() => {

            const emptyIndexes = newBoard.map((v, i) => v === null ? i : -1).filter(i => i !== -1);
            if (emptyIndexes.length === 0) {

                setState({ isThinking: false, message: formatMessage({ id: 'your_turn.f302daca', defaultMessage: 'Your turn!' }) });
                return;

            }
            const randomIndex = emptyIndexes[Math.floor(Math.random() * emptyIndexes.length)];
            newBoard[randomIndex] = 'O';
            setState({ board: newBoard });

            const winnerAfterAI = checkWinner(newBoard);
            if (winnerAfterAI) {

                setState({ message: winnerAfterAI === 'Tie' ? formatMessage({ id: 'its_a_tie.3670a5e4', defaultMessage: "It's a tie!" }) : formatMessage({ id: 'winner_wins.e72376bf', defaultMessage: '{winner} wins!' }, { winner: winnerAfterAI }) });

            } else {

                setState({ message: formatMessage({ id: 'your_turn.f302daca', defaultMessage: 'Your turn!' }) });

            }
            setState({ isThinking: false });

        }, 2000 + Math.random() * 1000); // 2�3 second delay

    };

    const resetGame = () => {

        setState({ board: Array(9).fill(null), isThinking: false, message: formatMessage({ id: 'your_turn.f302daca', defaultMessage: 'Your turn!' }) });

    };

    const renderCell = (i: number) => (
        <button
            style={{
                alignItems: 'center',
                backgroundColor: '#f8f9fa',
                border: '2px solid #dee2e6',
                borderRadius: 8,
                boxShadow: board[i] ? 'inset 0 0 5px #ccc' : 'none',
                cursor: board[i] || isThinking ? 'default' : 'pointer',
                display: 'flex',
                fontSize: 24,
                height: 60,
                justifyContent: 'center',
                margin: 4,
                width: 60
            }}
            key={i}
            onClick={() => handleClick(i)}
            type="button"
        >
            {board[i]}
        </button>
    );

    return (
        <div
            style={{
                backgroundColor: '#ffffff',
                borderRadius: 12,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                display: 'inline-block',
                padding: 16,
                textAlign: 'center'
            }}
        >
            <h5 style={{ marginBottom: 12 }}>
                <FormattedMessage defaultMessage="Tic-Tac-Toe" id="tic_tac_toe.1cfb9b3f" />
            </h5>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', justifyContent: 'center' }}>
                {board.map((_, i) => renderCell(i))}
            </div>
            <div style={{ marginTop: 12, minHeight: 24 }}>
                {message}
            </div>
            <button
                style={{
                    backgroundColor: '#007bff',
                    border: 'none',
                    borderRadius: 6,
                    color: '#fff',
                    cursor: 'pointer',
                    marginTop: 12,
                    padding: '6px 12px'
                }}
                onClick={resetGame}
                type="button"
            >
                <FormattedMessage defaultMessage="Reset" id="reset.43ed980c" />
            </button>
        </div>
    );

};